import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import { useDeleteSession } from '../api/useDeleteSession';
import { useListSessions } from '../api/useListSessions';
import { useResendSession } from '../api/useResendSession';
import { useResendWorkflowInvite } from '../api/useResendWorkflowInvite';
import { SessionsTable } from '../components/SessionsTable';
import { SessionsToolbar } from '../components/SessionsToolbar';
import { StartSessionModal } from '../components/StartSessionModal';
import type { SessionDto } from '../schemas/sessionSchemas';
import { displayApplicantName } from '../sessionReference';

// The filter chips offer the SEVEN Original Status values — our system's own verdict
// on a run — which the backend resolves (including the three derived from locks and
// the validity window). Sent comma-joined, so chips combine as an OR.
const STATUS_FILTERS: Array<{ key: string; label: string }> = [
  { key: 'NotStarted', label: 'Not Started' },
  { key: 'InProgress', label: 'In Progress' },
  { key: 'Locked', label: 'Locked' },
  { key: 'Expired', label: 'Expired' },
  { key: 'InReview', label: 'In Review' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Rejected', label: 'Rejected' },
];

// There is no separate "Invited · Not started" chip any more. An invite that was emailed
// and never opened comes back from the sessions endpoint as a Not Started row, so it is
// already in the default view and already covered by the Not Started chip — a second
// control for the same rows only raised the question of which one to trust.
const FILTER_OPTIONS = STATUS_FILTERS;

/** How long to wait after the last keystroke before querying. */
const SEARCH_DEBOUNCE_MS = 350;

export function SessionsListScreen() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [startOpen, setStartOpen] = useState(false);

  // Filters, search and selection.
  const [filters, setFilters] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Delete confirmation covers one row or many — `null` means the dialog is closed.
  const [pendingDelete, setPendingDelete] = useState<SessionDto[] | null>(null);

  const deleteSession = useDeleteSession();
  const resendSession = useResendSession();
  const resendWorkflowInvite = useResendWorkflowInvite();

  // Debounce the search box: one query per pause, not one per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any change to what is being listed sends us back to page one — page 3 of the old
  // result set is meaningless against the new one. Tracked against a signature rather
  // than the raw arrays so a re-render with an equal-but-new array is not a change.
  const querySignature = `${filters.join(',')}|${search}`;
  const prevSignature = useRef(querySignature);
  useEffect(() => {
    if (prevSignature.current !== querySignature) {
      prevSignature.current = querySignature;
      setPage(1);
      // Selections refer to rows that may no longer be listed.
      setSelectedIds([]);
    }
  }, [querySignature]);

  // One query for the whole list. The endpoint folds in unopened invites, so there is no
  // second request to switch between and no `enabled` gate.
  const { data, isLoading, isFetching, refetch } = useListSessions({
    page,
    pageSize,
    search: search || undefined,
    status: filters.length > 0 ? filters.join(',') : undefined,
  });

  const sessions: SessionDto[] = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  // Chips combine as an OR. No chip is exclusive any more, now that the invites view is
  // not a separate query.
  function addFilter(key: string) {
    setFilters((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }

  /**
   * Resend routes by what the row IS. An unopened invite has no session, so its id is an
   * invite id and only the invite endpoint accepts it; sending it to the session endpoint
   * would 404. Both spin the same row via `resendingId` below.
   */
  function handleResend(session: SessionDto) {
    const recipient = session.inviteEmail ?? 'the recipient';
    if (session.rowKind === 'Invite') {
      resendWorkflowInvite.mutate(session.id, {
        onSuccess: () => toast.success(`Invite sent again to ${recipient}`),
        onError: () => toast.error('Could not send the invite again'),
      });
      return;
    }
    resendSession.mutate(session.id, {
      onSuccess: () => toast.success(`Reminder sent to ${recipient}`),
      onError: () => toast.error('Could not send the reminder'),
    });
  }

  // Whichever resend is in flight owns the spinner, so only that row spins.
  const resendingId = resendSession.isPending
    ? (resendSession.variables ?? null)
    : resendWorkflowInvite.isPending
      ? (resendWorkflowInvite.variables ?? null)
      : null;

  function requestDeleteSelected() {
    const rows = sessions.filter((s) => selectedIds.includes(s.id));
    if (rows.length === 0) {
      toast.error('Select at least one session to delete');
      return;
    }
    setPendingDelete(rows);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    // No bulk endpoint exists, so this is n deletes. Settled rather than all: one
    // failure must not hide the ones that succeeded, and the count reported back is
    // what actually happened.
    const results = await Promise.allSettled(
      pendingDelete.map((s) => deleteSession.mutateAsync(s.id)),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    const deleted = results.length - failed;

    if (deleted > 0) {
      toast.success(deleted === 1 ? 'Session deleted' : `${deleted} sessions deleted`);
    }
    if (failed > 0) {
      toast.error(
        failed === 1 ? 'One session could not be deleted' : `${failed} could not be deleted`,
      );
    }

    setSelectedIds((prev) => prev.filter((id) => !pendingDelete.some((s) => s.id === id)));
    setPendingDelete(null);
  }

  const deleteCount = pendingDelete?.length ?? 0;

  return (
    <AppShell
      title="Sessions"
      subtitle="View and manage verification sessions across all your verification workflows"
    >
      <div className="flex w-full flex-col gap-[clamp(0.75rem,1vw,1rem)] text-black-logo lg:h-full lg:overflow-hidden">
        <SessionsToolbar
          search={searchInput}
          onSearchChange={setSearchInput}
          filters={filters}
          filterOptions={FILTER_OPTIONS}
          onAddFilter={addFilter}
          onRemoveFilter={(key) => setFilters((prev) => prev.filter((f) => f !== key))}
          onClearFilters={() => setFilters([])}
          selectedCount={selectedIds.length}
          onDeleteSelected={requestDeleteSelected}
          onRefresh={() => refetch()}
          isRefreshing={isFetching}
          onStartSession={() => setStartOpen(true)}
        />

        {/* ONE table. Unopened invites arrive from the same endpoint as Not Started rows,
            so there is no separate invites view to switch to — the default list shows
            everything and the filter chips narrow it. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <SessionsTable
            sessions={sessions}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onToggleRow={(id) =>
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
              )
            }
            onToggleAll={(ids) =>
              setSelectedIds((prev) => (ids.every((id) => prev.includes(id)) ? [] : ids))
            }
            onResend={handleResend}
            resendingId={resendingId}
          />
        </div>

        {totalCount > 0 && (
          <div className="mt-auto flex shrink-0 flex-wrap items-center justify-between gap-[clamp(12px,1vw,16px)] pt-1">
            <div className="text-[length:var(--text-14)] text-[#666666]">
              Showing {rangeStart} - {rangeEnd} of {totalCount} Session
              {totalCount === 1 ? '' : 's'}
            </div>

            <div className="flex items-center gap-1.5">
              <PageButton
                nav
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                label="Previous page"
              >
                <ChevronLeft size={16} />
              </PageButton>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => Math.abs(p - page) <= 1 || p === 1 || p === totalPages)
                .map((p) => (
                  <PageButton
                    key={p}
                    active={p === page}
                    onClick={() => setPage(p)}
                    label={`Page ${p}`}
                  >
                    {p}
                  </PageButton>
                ))}
              <PageButton
                nav
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                label="Next page"
              >
                <ChevronRight size={16} />
              </PageButton>
            </div>

            <div className="flex items-center gap-2 text-[length:var(--text-14)] text-[#666666]">
              Show
              <span className="inline-flex w-[clamp(48px,3.8vw,56px)] items-center justify-between rounded-lg border border-[#CCDAFF] bg-transparent px-2 py-0.5 text-[length:var(--text-14)] font-medium leading-tight text-[#0048FF]">
                {pageSize} <ChevronDown size={14} />
              </span>
            </div>
          </div>
        )}
      </div>

      <StartSessionModal open={startOpen} onClose={() => setStartOpen(false)} />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={
          deleteCount === 1
            ? `Delete session for "${displayApplicantName(pendingDelete?.[0]?.applicantFullName)}"?`
            : `Delete ${deleteCount} sessions?`
        }
        description="This permanently deletes the session and all its step data. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isPending={deleteSession.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </AppShell>
  );
}

/** The design's 24px pagination button: plain, active-ringed, or grey-filled nav. */
function PageButton({
  children,
  onClick,
  label,
  active,
  nav,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  active?: boolean;
  nav?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded-md border-none text-[length:var(--text-14)] font-medium leading-tight transition-colors disabled:opacity-40',
        active
          ? 'bg-white text-[#0048FF] ring-1 ring-[#0048FF]'
          : nav
            ? 'bg-[#F4F2F2] text-[#666666] hover:bg-[#E5E7EB]'
            : 'bg-transparent text-[#666666] hover:bg-gray-100',
      )}
    >
      {children}
    </button>
  );
}
